import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../utils/api';

const PaymentStatusPrint = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [meta, setMeta] = useState({});
    const location = useLocation();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get(`/reports/payment-status${location.search}`);
                setData(response.data?.data || []);
                setMeta({
                    academicYear: response.data?.academic_year,
                    region: response.data?.region,
                    institution: response.data?.institution,
                    months: response.data?.months || []
                });
                setLoading(false);
                
                // Trigger print after data is loaded and rendered
                setTimeout(() => {
                    window.print();
                }, 1000);
            } catch (err) {
                console.error("Error fetching payment status for print:", err);
                setLoading(false);
            }
        };

        fetchData();
    }, [location.search]);

    if (loading) {
        return <div className="p-10 text-center">جاري تحميل البيانات...</div>;
    }

    const monthsToDisplay = [
        { id: '09', name: '9' },
        { id: '10', name: '10' },
        { id: '11', name: '11' },
        { id: '12', name: '12' },
        { id: '01', name: '1' },
        { id: '02', name: '2' },
        { id: '03', name: '3' },
        { id: '04', name: '4' },
        { id: '05', name: '5' },
        { id: '06', name: '6' }
    ];

    const monthMap = {
        '9': 'سبتمبر', '10': 'أكتوبر', '11': 'نوفمبر', '12': 'ديسمبر',
        '1': 'يناير', '2': 'فبراير', '3': 'مارس', '4': 'أبريل',
        '5': 'مايو', '6': 'يونيو'
    };

    const totalRegistrationFees = data.reduce((sum, item) => sum + (Number(item.registration_fee) || 0), 0);

    const columnTotals = monthsToDisplay.reduce((acc, month) => {
        acc[month.name] = data.reduce((sum, item) => sum + (item.payments[monthMap[month.name]] || 0), 0);
        return acc;
    }, {});

    const globalTotal = data.reduce((sum, item) => sum + item.total, 0);

    return (
        <div className="print-page" dir="rtl">
            <div className="header">
                <div className="association-info">
                    <img src="/logo.png" alt="Logo" className="logo" />
                    <div className="text text-right">
                        <h1>جمعية تنمية النقل المدرسي</h1>
                        <h2>ولاد دحو</h2>
                    </div>
                </div>
                <div className="report-title">
                    <h3>حالة مدفوعات التلاميذ</h3>
                    <div className="filters-summary">
                        السنة الدراسية: {meta.academicYear}
                        <span> | المنطقة: {meta.region}</span>
                        {meta.institution && <span> | المؤسسة: {meta.institution}</span>}
                        <span> | العدد الإجمالي: {data.length} تلميذ</span>
                    </div>
                </div>
            </div>

            <table className="print-table">
                <thead>
                    <tr>
                        <th rowSpan="2" style={{ width: '18%' }}>NOM ET PRENOM</th>
                        <th rowSpan="2" style={{ width: '8%' }}>CODE QR</th>
                        <th rowSpan="2" style={{ width: '8%' }}>
                            ADHÉSION
                            <div style={{ fontSize: '9px', fontWeight: 'normal' }}>واجب الانخراط</div>
                        </th>
                        <th colSpan="10">MOIS</th>
                        <th rowSpan="2" style={{ width: '8%' }}>TOTAL</th>
                    </tr>
                    <tr>
                        {monthsToDisplay.map(m => (
                            <th key={m.id} style={{ width: '5.8%' }}>{m.name}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => {
                        const regFee = Number(item.registration_fee) || 0;
                        return (
                            <tr key={index}>
                                <td className="text-right font-bold">{item.full_name}</td>
                                <td>{item.qr_code}</td>
                                <td className={regFee > 0 ? 'font-bold' : 'text-slate-400'}>
                                    {regFee > 0 ? regFee.toFixed(2).replace('.', ',') : '0,00'}
                                </td>
                                {monthsToDisplay.map(m => {
                                    const amount = item.payments[monthMap[m.name]] || 0;
                                    return (
                                        <td key={m.id} className={amount > 0 ? 'font-bold' : 'text-slate-400'}>
                                            {amount > 0 ? amount.toFixed(2).replace('.', ',') : '0,00'}
                                        </td>
                                    );
                                })}
                                <td className="font-bold bg-slate-50">{item.total.toFixed(2).replace('.', ',')}</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr className="bg-slate-50 font-bold">
                        <td colSpan="2">TOTAL</td>
                        <td>
                            {totalRegistrationFees.toFixed(2).replace('.', ',')}
                        </td>
                        {monthsToDisplay.map(m => (
                            <td key={m.id}>
                                {columnTotals[m.name].toFixed(2).replace('.', ',')}
                            </td>
                        ))}
                        <td className="bg-yellow-300">
                            {globalTotal.toFixed(2).replace('.', ',')}
                        </td>
                    </tr>
                </tfoot>
            </table>

            <div className="footer">
                <p>تاريخ الاستخراج: {new Date().toLocaleDateString('ar-MA')}</p>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@400;700&display=swap');
                
                @page {
                    size: A4 landscape;
                    margin: 10mm;
                }

                body {
                    font-family: 'Noto Kufi Arabic', sans-serif;
                    margin: 0;
                    padding: 0;
                    color: #333;
                }

                .print-page {
                    width: 100%;
                }

                .header {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: 20px;
                    border-bottom: 2px solid #eea508;
                    padding-bottom: 10px;
                }

                .association-info {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 20px;
                    width: 100%;
                    margin-bottom: 10px;
                }

                .logo {
                    height: 60px;
                    object-fit: contain;
                }

                .header h1 {
                    margin: 0;
                    font-size: 18px;
                    color: #1e293b;
                }

                .header h2 {
                    margin: 0;
                    font-size: 14px;
                    color: #eea508;
                }

                .report-title {
                    text-align: center;
                }

                .report-title h3 {
                    margin: 5px 0;
                    font-size: 16px;
                    text-decoration: underline;
                }

                .filters-summary {
                    font-size: 10px;
                    color: #64748b;
                    margin-top: 2px;
                }

                .filters-summary span {
                    margin-right: 15px;
                }

                .print-table {
                    width: 100%;
                    border-collapse: collapse;
                    border: 2px solid #000;
                }

                .print-table th, .print-table td {
                    border: 1px solid #000;
                    padding: 6px 4px;
                    text-align: center;
                    font-size: 10px;
                }

                .print-table th {
                    background-color: #fff;
                    font-weight: 700;
                    text-transform: uppercase;
                }

                .bg-slate-50 {
                    background-color: #f8fafc !important;
                    -webkit-print-color-adjust: exact;
                }

                .bg-yellow-300 {
                    background-color: #fde047 !important;
                    -webkit-print-color-adjust: exact;
                }

                .text-right {
                    text-align: right !important;
                    padding-right: 8px !important;
                }

                .font-bold {
                    font-weight: 700;
                }

                .footer {
                    margin-top: 20px;
                    text-align: left;
                    font-size: 9px;
                    color: #94a3b8;
                }
            `}} />
        </div>
    );
};

export default PaymentStatusPrint;
